<template>
  <div
    v-if="isVisible"
    class="demo-confirmation-overlay"
    @click.self="handleCancel"
  >
    <div
      class="demo-confirmation-dialog"
      role="alertdialog"
      aria-labelledby="confirmation-title"
      aria-describedby="confirmation-message"
    >
      <!-- Header -->
      <div class="demo-confirmation__header">
        <h2
          id="confirmation-title"
          class="demo-confirmation__title"
        >
          {{ title }}
        </h2>
        <button
          class="demo-confirmation__close"
          aria-label="Close dialog"
          @click="handleCancel"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Message -->
      <div class="demo-confirmation__body">
        <p
          id="confirmation-message"
          class="demo-confirmation__message"
        >
          {{ message }}
        </p>
      </div>

      <!-- Footer with action buttons -->
      <div class="demo-confirmation__footer">
        <button
          v-if="showCancelButton"
          class="demo-confirmation__button demo-confirmation__button--cancel"
          @click="handleCancel"
        >
          {{ cancelButtonLabel }}
        </button>

        <button
          class="demo-confirmation__button demo-confirmation__button--secondary"
          @click="handleExit"
        >
          {{ exitButtonLabel }}
        </button>

        <button
          class="demo-confirmation__button demo-confirmation__button--primary"
          @click="handleContinue"
        >
          {{ continueButtonLabel }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  DEMO_CONFIRMATION_CONFIG,
} from '@/demo/constants';

/**
 * DemoConfirmationDialog Component
 * Modal dialog for confirming out-of-scope interactions during demo
 * Shows when user performs actions outside the guided flow and asks to continue or exit
 *
 * @component
 * @example
 * <DemoConfirmationDialog
 *   :visible="showConfirmation"
 *   message-type="out-of-scope"
 *   @continue="handleContinueDemo"
 *   @exit="handleExitDemo"
 *   @close="closeDialog"
 * />
 */
export default {
  name: 'DemoConfirmationDialog',

  props: {
    /**
     * Whether the dialog is visible
     */
    visible: {
      type: Boolean,
      default: false,
    },

    /**
     * Type of confirmation message
     * Options: 'out-of-scope', 'invalid-interaction', 'navigation-warning'
     */
    messageType: {
      type: String,
      default: 'out-of-scope',
      validator: (value) => ['out-of-scope', 'invalid-interaction', 'navigation-warning'].includes(value),
    },

    /**
     * Custom message to display (overrides messageType)
     */
    customMessage: {
      type: String,
      default: '',
    },

    /**
     * Custom title (overrides default)
     */
    customTitle: {
      type: String,
      default: '',
    },

    /**
     * Show Cancel button
     */
    showCancelButton: {
      type: Boolean,
      default: true,
    },
  },

  emits: {
    /**
     * When user chooses to continue the demo
     */
    continue: null,

    /**
     * When user chooses to exit the demo
     */
    exit: null,

    /**
     * When user closes the dialog
     */
    close: null,

    /**
     * Update visible state (v-model support)
     */
    'update:visible': null,
  },

  data() {
    return {
      DEMO_CONFIRMATION_CONFIG,
    };
  },

  computed: {
    /**
     * Dialog visibility
     */
    isVisible() {
      return this.visible;
    },

    /**
     * Dialog title
     */
    title() {
      return this.customTitle || DEMO_CONFIRMATION_CONFIG.TITLE;
    },

    /**
     * Dialog message based on type
     */
    message() {
      if (this.customMessage) {
        return this.customMessage;
      }

      const messages = DEMO_CONFIRMATION_CONFIG.MESSAGES;
      switch (this.messageType) {
        case 'invalid-interaction':
          return messages.INVALID_INTERACTION;
        case 'navigation-warning':
          return messages.NAVIGATION_WARNING;
        case 'out-of-scope':
        default:
          return messages.OUT_OF_SCOPE;
      }
    },

    /**
     * "Continue Demo" button label
     */
    continueButtonLabel() {
      return DEMO_CONFIRMATION_CONFIG.BUTTONS.CONTINUE.label;
    },

    /**
     * "Exit & Proceed" button label
     */
    exitButtonLabel() {
      return DEMO_CONFIRMATION_CONFIG.BUTTONS.EXIT.label;
    },

    /**
     * "Cancel" button label
     */
    cancelButtonLabel() {
      return DEMO_CONFIRMATION_CONFIG.BUTTONS.CANCEL.label;
    },

  },

  watch: {
    /**
     * When dialog becomes visible, reset checkbox state
     */
    visible(newVal) {
      if (newVal) {
        this.dontAskAgain = false;
        this.$nextTick(() => {
          // Focus on continue button for accessibility
          this.$el.querySelector('.demo-confirmation__button--primary')?.focus();
        });
      }
    },
  },

  mounted() {
    // Handle escape key to close dialog
    document.addEventListener('keydown', this.handleKeydown);
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown);
  },

  methods: {
    /**
     * Handle continue button click
     */
    handleContinue() {
      this.$emit('continue');
      this.closeDialog();
    },

    /**
     * Handle exit button click
     */
    handleExit() {
      this.$emit('exit');
      this.closeDialog();
    },

    /**
     * Handle cancel button click or dialog close
     */
    handleCancel() {
      this.$emit('close');
      this.closeDialog();
    },

    /**
     * Close the dialog
     */
    closeDialog() {
      this.$emit('update:visible', false);
    },

    /**
     * Handle keyboard events (Escape to close)
     */
    handleKeydown(event) {
      if (event.key === 'Escape' && this.isVisible) {
        event.preventDefault();
        this.handleCancel();
      }
    },

  },
};
</script>

<style scoped lang="scss">
.demo-confirmation-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fade-in 300ms ease-out;
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.demo-confirmation-dialog {
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 90vw;
  animation: slide-up 300ms ease-out;
  max-height: 80vh;
  overflow-y: auto;
}

@keyframes slide-up {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.demo-confirmation__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #e8e8e8;
}

.demo-confirmation__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333333;
  flex: 1;
}

.demo-confirmation__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: none;
  border: none;
  cursor: pointer;
  color: #999999;
  border-radius: 6px;
  transition: all 200ms ease;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    background-color: #f5f5f5;
    color: #333333;
  }

  &:focus-visible {
    outline: 2px solid #007aff;
    outline-offset: 2px;
  }
}

.demo-confirmation__body {
  padding: 20px;
}

.demo-confirmation__message {
  margin: 0 0 16px 0;
  font-size: 14px;
  color: #666666;
  line-height: 1.6;
}

.demo-confirmation__footer {
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #e8e8e8;
  justify-content: flex-end;
}

.demo-confirmation__button {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms ease;
  white-space: nowrap;

  &:focus-visible {
    outline: 2px solid #007aff;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.demo-confirmation__button--primary {
  background-color: #007aff;
  color: #ffffff;

  &:hover:not(:disabled) {
    background-color: #0051d5;
    box-shadow: 0 2px 8px rgba(0, 122, 255, 0.3);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }
}

.demo-confirmation__button--secondary {
  background-color: #e8e8e8;
  color: #333333;

  &:hover:not(:disabled) {
    background-color: #d0d0d0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }
}

.demo-confirmation__button--cancel {
  background-color: transparent;
  color: #666666;
  border: 1px solid #e8e8e8;

  &:hover:not(:disabled) {
    background-color: #f5f5f5;
    border-color: #d0d0d0;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }
}

@media (max-width: 480px) {
  .demo-confirmation-dialog {
    width: calc(100vw - 32px);
    max-height: calc(100vh - 32px);
  }

  .demo-confirmation__footer {
    flex-direction: column;

    .demo-confirmation__button {
      width: 100%;
    }
  }
}
</style>
